// Gates network-dependent rendering until the active identity backend
// (Auth0 in the browser, native bridge in a WebView) has produced a usable
// access token. Backend-agnostic: takes whatever `useIdentity()` returns, so
// it works the same regardless of which IdentityState implementation is
// mounted. The token itself is fetched fresh per request by
// InterceptorInstaller; this hook only tracks first-fetch readiness so
// NetworkProvider can hold children/LoginPage until that resolves.

import { useEffect, useState } from 'react';
import { useClientId } from '../../hooks/useClientId';
import type { IdentityState } from '../../auth';

export interface NetworkAuthReadinessState {
  isNetworkAuthReady: boolean;
  /** True once the current client's token fetch has failed — the session is gone and cannot be retried against. */
  hasAuthFailed: boolean;
}

export function useNetworkAuthReadiness({
  isAuthenticated,
  getAccessToken,
}: Pick<
  IdentityState,
  'isAuthenticated' | 'getAccessToken'
>): NetworkAuthReadinessState {
  // The client ID whose token fetch last succeeded, not a bare boolean —
  // deriving readiness from `readyClientId === clientId` means a clientId
  // change is unready the instant it renders, with no separate flag to
  // remember to clear: a stale client's success can never satisfy a
  // different current client.
  const [readyClientId, setReadyClientId] = useState<string | null>(null);
  // Same reasoning as readyClientId: the client ID the failure belongs to,
  // not a bare boolean — otherwise a clientId change would keep reporting
  // the previous client's failure until the effect below re-runs and clears
  // it, showing the session-expired page for a client that never failed.
  const [failedClientId, setFailedClientId] = useState<string | null>(null);
  const clientId = useClientId();

  useEffect(() => {
    if (!isAuthenticated) {
      setReadyClientId(null);
      setFailedClientId(null);
      return;
    }

    let cancelled = false;
    const authorizationParams: Record<string, string> = {};
    if (clientId) {
      authorizationParams.l_client_id = clientId;
    }

    getAccessToken({ authorizationParams })
      .then(() => {
        if (!cancelled) {
          setReadyClientId(clientId);
          setFailedClientId(null);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setReadyClientId(null);
          setFailedClientId(clientId);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, getAccessToken, clientId]);

  return {
    isNetworkAuthReady: isAuthenticated && readyClientId === clientId,
    hasAuthFailed: isAuthenticated && failedClientId === clientId,
  };
}
